import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsConnectCdnService } from '@/api/operations-org-hooks';
import type { OpsCdnConnectionResult, OpsEagleConnection } from '@/api/operations';

type RowState =
  | { kind: 'running' }
  | { kind: 'done'; result: OpsCdnConnectionResult; forced: boolean }
  | { kind: 'checked'; result: OpsCdnConnectionResult }
  | { kind: 'failed'; message: string };

interface Props {
  orgId: string;
  /** Every Eagle service of the org with its connection state. */
  services: OpsEagleConnection[];
  /** Service the dialog was opened from; preselected on its own. */
  focusId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const errorText = (e: unknown): string => {
  const r = (e as { response?: { data?: { error_message?: string } } } | null)?.response;
  return r?.data?.error_message ?? 'Request failed.';
};

/** How well the stored key matches the domains of the service. */
function keyHealth(r: OpsCdnConnectionResult): { tone: 'success' | 'warning'; text: string } {
  if (r.verified === false) return { tone: 'warning', text: 'the vendor did not accept the key' };
  const total = r.domains_total ?? 0;
  const found = r.domains_found ?? 0;
  if (total === 0) return { tone: 'success', text: `key sees ${r.visible_services ?? 0} services` };
  if (found === total) return { tone: 'success', text: `key sees all ${total} domain${total === 1 ? '' : 's'}` };
  return { tone: 'warning', text: `key sees ${found} of ${total} domains` };
}

/**
 * Connect the Eagle services of an org to their own Flashcone account.
 *
 * A service on the shared reseller key cannot see its customer's services: every
 * attach/read on its domains fails with ServiceId.IdPermission. Connecting gives
 * the service a key for ITS OWN customer (its entity id), so an org holding many
 * customers (one per entity) is fixed one service at a time against the right
 * customer. A service already connected can be checked (does its key see the
 * service's domains?) and, when it does not, re-connected to mint a fresh key.
 * Services run one after the other from the browser, so a large org shows
 * progress and one failure never stops the rest.
 */
export function OpsEagleConnectDialog({ orgId, services, focusId, open, onOpenChange }: Props) {
  const connect = useOpsConnectCdnService();
  // Frozen at open: each success refreshes the fleet list, and a service must stay visible with its result.
  const [rows] = useState(() => services.filter((s) => s.status !== 'terminated'));
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(focusId ? [focusId] : rows.filter((s) => !s.connected).map((s) => s.service_id)),
  );
  const [reason, setReason] = useState('');
  const [state, setState] = useState<Record<string, RowState>>({});
  const [running, setRunning] = useState(false);

  const chosen = rows.filter((s) => picked.has(s.service_id));
  const customers = new Set(rows.map((s) => s.entity_id).filter(Boolean)).size;
  const notConnected = rows.filter((s) => !s.connected).length;
  const settled = (id: string) => ['done', 'failed'].includes(state[id]?.kind ?? '');
  const finished = chosen.length > 0 && chosen.every((s) => settled(s.service_id));
  const failedCount = Object.values(state).filter((r) => r.kind === 'failed').length;
  const reconnecting = chosen.filter((s) => s.connected).length;
  const canRun = chosen.length > 0 && reason.trim() !== '' && !running && !finished;

  const toggle = (id: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleClose = () => {
    if (running) return;
    onOpenChange(false);
    setState({});
    setReason('');
  };

  const call = async (s: OpsEagleConnection, force: boolean) => {
    setState((prev) => ({ ...prev, [s.service_id]: { kind: 'running' } }));
    try {
      const result = await connect.mutateAsync({ serviceId: s.service_id, reason: reason.trim(), force });
      setState((prev) => ({
        ...prev,
        [s.service_id]: force || !s.connected ? { kind: 'done', result, forced: force } : { kind: 'checked', result },
      }));
    } catch (e) {
      setState((prev) => ({ ...prev, [s.service_id]: { kind: 'failed', message: errorText(e) } }));
    }
  };

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    for (const s of chosen) {
      if (state[s.service_id]?.kind === 'done') continue;
      await call(s, s.connected);
    }
    setRunning(false);
  };

  const check = async (s: OpsEagleConnection) => {
    setRunning(true);
    await call(s, false);
    setRunning(false);
  };

  const describe = (s: OpsEagleConnection) => {
    const r = state[s.service_id];
    if (r?.kind === 'running') return <StatusPill tone="info">Working…</StatusPill>;
    if (r?.kind === 'failed') return <StatusPill tone="danger">Failed</StatusPill>;
    if (r?.kind === 'done' || r?.kind === 'checked') {
      const h = keyHealth(r.result);
      const how =
        r.kind === 'checked' ? 'checked' : r.result.source === 'sibling' ? 'key reused from the same customer' : 'new key';
      return (
        <StatusPill tone={h.tone}>
          Connected ({how}; {h.text})
        </StatusPill>
      );
    }
    return s.connected ? <StatusPill tone="neutral">Connected</StatusPill> : <StatusPill tone="warning">Not connected</StatusPill>;
  };

  const needsRepair = Object.values(state).some(
    (r) => r.kind === 'checked' && keyHealth(r.result).tone === 'warning',
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Eagle accounts</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Alert tone="info">
              Org {orgId} has {rows.length} Eagle service{rows.length === 1 ? '' : 's'} across {customers} Flashcone
              customer{customers === 1 ? '' : 's'}; {notConnected} still use{notConnected === 1 ? 's' : ''} the shared
              reseller key. Each service gets a key for its own customer: this creates or reuses that customer's
              support admin account, resets its password and mints an access key at the vendor. A connected service
              you tick again is re-connected with a fresh key.
            </Alert>

            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rows.length === 0 && <span>This org has no Eagle services.</span>}
              {rows.map((s) => (
                <div
                  key={s.service_id}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}
                >
                  <Checkbox
                    checked={picked.has(s.service_id)}
                    disabled={running || state[s.service_id]?.kind === 'done'}
                    onCheckedChange={(v) => toggle(s.service_id, v === true)}
                  />
                  <span style={{ flex: '1 1 220px' }}>
                    {s.service_name}
                    <span style={{ color: 'var(--vn-fg-muted)' }}> · customer {s.entity_id || '—'}</span>
                  </span>
                  {describe(s)}
                  {s.connected && (
                    <Button variant="ghost" size="sm" disabled={running || reason.trim() === ''} onClick={() => check(s)}>
                      Check
                    </Button>
                  )}
                  {state[s.service_id]?.kind === 'failed' && (
                    <span style={{ color: 'var(--vn-fg-danger, #b42318)', flexBasis: '100%' }}>
                      {(state[s.service_id] as { kind: 'failed'; message: string }).message}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <Field label="Reason (required)">
              {(p) => (
                <Input
                  {...p}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Fix ServiceId.IdPermission when attaching certificates"
                  disabled={running}
                />
              )}
            </Field>

            {needsRepair && (
              <Alert tone="warning">
                A checked service whose key does not see all of its domains probably holds a key for another
                customer. Tick it and run again to re-connect it.
              </Alert>
            )}
            {finished && (
              <Alert tone={failedCount > 0 ? 'warning' : 'success'}>
                {failedCount > 0
                  ? `${failedCount} service${failedCount === 1 ? '' : 's'} failed. Fix the cause and run again: finished services are skipped.`
                  : 'All selected services are done.'}
              </Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={running}>
            {finished ? 'Close' : 'Cancel'}
          </Button>
          <Button variant="primary" size="sm" onClick={run} disabled={!canRun}>
            {running
              ? 'Working…'
              : reconnecting > 0
                ? `Connect / re-connect ${chosen.length} service${chosen.length === 1 ? '' : 's'}`
                : `Connect ${chosen.length} service${chosen.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
